import 'styles/main.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/tiptap/styles.css';
import '@mantine/dropzone/styles.css';
import { RouterProvider } from 'react-router-dom';
import routes from 'routes';
import { useEffect } from 'react';
import { initAnalytics, trackPageview } from 'utils/analytics';

function App() {
  useEffect(() => {
    let unsub: undefined | (() => void);
    let mounted = true;
    let lastPath: string | null = null;
    // keep original history methods to restore on cleanup
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    let removePopstate: (() => void) | undefined;

    initAnalytics().then(() => {
      if (!mounted) return;
      const send = (loc: { pathname: string; search: string }) => {
        const path = `${loc.pathname}${loc.search}`;
        if (path !== lastPath) {
          trackPageview(path);
          lastPath = path;
        }
      };

      // Initial page view via router state if available
      const currentLoc = (routes as any).state?.location || {
        pathname: window.location.pathname,
        search: window.location.search
      };
      send(currentLoc);

      // Primary: subscribe to router (supported on data routers in many RR versions)
      // @ts-ignore - router.subscribe may not be typed depending on version
      unsub = (routes as any).subscribe?.((state: any) => {
        if (state?.location) send(state.location);
      });

      // Fallback: observe History API changes so SPA navigations are tracked
      if (!unsub) {
        const notifyFromLocation = () => {
          send({ pathname: window.location.pathname, search: window.location.search });
        };

        // wrap pushState/replaceState without relying on implicit `this`
        window.history.pushState = ((...args: Parameters<typeof originalPushState>) => {
          originalPushState.apply(window.history, args);
          notifyFromLocation();
        }) as typeof window.history.pushState;

        window.history.replaceState = ((...args: Parameters<typeof originalReplaceState>) => {
          originalReplaceState.apply(window.history, args);
          notifyFromLocation();
        }) as typeof window.history.replaceState;

        const onPopState = () => notifyFromLocation();
        window.addEventListener('popstate', onPopState);
        removePopstate = () => window.removeEventListener('popstate', onPopState);
      }
    });

    return () => {
      mounted = false;
      if (typeof unsub === 'function') unsub();
      // restore history methods & listeners
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      removePopstate?.();
    };
  }, []);

  return <RouterProvider router={routes} />;
}

export default App;
